'use client'

import { useEffect } from 'react'
import type { ToolKey, ToolState } from '@/types/radar'
import { TOOLS as TOOLS_DATA } from '@/types/radar'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import { hasUsableScore } from '@/lib/radar/tool-scores'
import { ToolDetailPanel } from './ToolDetailPanel'
import {
  Bot,
  Shield,
  FileText,
  Zap,
  Quote,
  MessageCircle,
  ScanSearch,
  SearchCheck,
  Waypoints,
  BarChart3,
  ShieldCheck,
  BrainCircuit,
  Fingerprint,
} from 'lucide-react'

const TOOL_ICONS: Record<string, typeof Bot> = {
  crawl: Bot,
  robots: Shield,
  llms: FileText,
  readiness: Zap,
  citation: Quote,
  reddit: MessageCircle,
  aeo: ScanSearch,
  answer: SearchCheck,
  sourceInfluence: Waypoints,
  promptSov: BarChart3,
  schemaAudit: ShieldCheck,
  hallucinationCheck: BrainCircuit,
  brandDisambiguation: Fingerprint,
}

const TOOLS_WITH_ICONS = TOOLS_DATA.map(t => ({
  ...t,
  icon: TOOL_ICONS[t.key] || Bot,
}))

export function DetailsTab({
  tools,
  expandedTool,
  setExpandedTool,
}: {
  tools: Record<ToolKey, ToolState>
  expandedTool: ToolKey | null
  setExpandedTool: (key: ToolKey | null) => void
}) {
  const doneTKeys = TOOLS_WITH_ICONS.filter(t => tools[t.key].status === 'done')

  useEffect(() => {
    if (!expandedTool && doneTKeys.length > 0) {
      setExpandedTool(doneTKeys[0].key as ToolKey)
    }
  }, [expandedTool, doneTKeys, setExpandedTool])

  return (
    <div className='space-y-4'>
      <div className='flex gap-2 flex-wrap'>
        {doneTKeys.map(t => {
          const Icon = t.icon
          const state = tools[t.key as ToolKey]
          const scorable = hasUsableScore(state)
          const grade = scorable ? (state.data.grade as string) : undefined
          return (
            <button
              key={t.key}
              onClick={() =>
                setExpandedTool(
                  expandedTool === t.key ? null : (t.key as ToolKey)
                )
              }
              className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all'
              style={{
                borderColor:
                  expandedTool === t.key ? `${t.color}60` : 'var(--border)',
                background:
                  expandedTool === t.key ? `${t.color}10` : 'var(--card)',
                color:
                  expandedTool === t.key ? t.color : 'var(--muted-foreground)',
              }}
            >
              <Icon size={12} />
              {t.name}
              <span
                className='font-bold'
                style={{
                  color: grade
                    ? GRADE_COLORS[grade]
                    : 'var(--muted-foreground)',
                }}
              >
                {scorable ? state.data.overallScore : '--'}
              </span>
            </button>
          )
        })}
      </div>
      {expandedTool &&
        tools[expandedTool].status === 'done' &&
        (() => {
          const t = TOOLS_WITH_ICONS.find(tool => tool.key === expandedTool)
          return (
            <ToolDetailPanel
              toolKey={expandedTool}
              data={tools[expandedTool].data}
              toolIcon={t?.icon || Bot}
              toolColor={t?.color || '#475569'}
              toolName={t?.name || expandedTool}
            />
          )
        })()}
    </div>
  )
}
